import {
  ArrowRightUpLine,
  GoogleLine,
  ListCheckLine,
  MailLine,
  Message3Line,
  Table2Line,
  WebhookLine,
  WhatsappLine,
} from "@mingcute/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import ProductAppCard, { ProductAppCardSkeleton } from "../ProductAppCard";

function isImageSource(icon) {
  return (
    typeof icon === "string" &&
    (icon.startsWith("http://") ||
      icon.startsWith("https://") ||
      icon.startsWith("/") ||
      icon.startsWith("data:image/") ||
      /\.(png|jpe?g|gif|svg|webp)(\?|#|$)/i.test(icon))
  );
}

function normalizeOptionToken(option) {
  return [
    option?.key,
    option?.icon,
    option?.title,
    option?.subtitle,
    option?.tag,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const optionIconConfig = {
  api: {
    Icon: WebhookLine,
    bg: "feedback.info.surface",
    border: "feedback.info.border",
    color: "feedback.info.text",
  },
  default: {
    Icon: ListCheckLine,
    bg: "feedback.neutral.surface",
    border: "feedback.neutral.border",
    color: "feedback.neutral.text",
  },
  email: {
    Icon: MailLine,
    bg: "feedback.info.surface",
    border: "feedback.info.border",
    color: "feedback.info.text",
  },
  erp: {
    Icon: ListCheckLine,
    bg: "feedback.warning.surface",
    border: "feedback.warning.border",
    color: "feedback.warning.text",
  },
  excel: {
    Icon: Table2Line,
    bg: "feedback.success.surface",
    border: "feedback.success.border",
    color: "feedback.success.text",
  },
  google: {
    Icon: GoogleLine,
    bg: "feedback.info.surface",
    border: "feedback.info.border",
    color: "feedback.info.text",
  },
  sms: {
    Icon: Message3Line,
    bg: "feedback.warning.surface",
    border: "feedback.warning.border",
    color: "feedback.warning.text",
  },
  whatsapp_api: {
    Icon: WhatsappLine,
    bg: "feedback.success.surface",
    border: "feedback.success.border",
    color: "feedback.success.text",
  },
};

function resolveIconKind(option) {
  const token = normalizeOptionToken(option);

  if (token.includes("whatsapp")) return "whatsapp_api";
  if (token.includes("sms")) return "sms";
  if (token.includes("e-mail") || token.includes("email")) return "email";
  if (token.includes("google")) return "google";
  if (token.includes("excel") || token.includes("arquivo") || token.includes("newspaper")) return "excel";
  if (token.includes("api") || token.includes("webhook") || token.includes("requisição")) return "api";
  if (token.includes("erp") || token.includes("conector") || token.includes("integração erp")) return "erp";

  return "default";
}

function isEnabledStatus(status) {
  if (!status) return true;

  const normalizedStatus = String(status).trim().toLowerCase();

  return [
    "active",
    "ativa",
    "ativo",
    "configured",
    "configurado",
    "connected",
    "conectado",
  ].includes(normalizedStatus);
}

function isOptionEnabled(option) {
  const paid = typeof option?.alreadyPaid === "function"
    ? option.alreadyPaid()
    : option?.alreadyPaid;

  return Boolean(paid) && isEnabledStatus(option?.status);
}

function ProductOptionCard({ disabledTooltip, option }) {
  const enabled = isOptionEnabled(option);
  const iconIsImage = isImageSource(option?.icon);
  const iconConfig = optionIconConfig[resolveIconKind(option)] || optionIconConfig.default;
  const Icon = iconConfig.Icon;

  return (
    <ProductAppCard
      actionIcon={<ArrowRightUpLine style={{ height: 19, width: 19 }} />}
      disabled={!enabled}
      disabledTooltip={option?.disabledTooltip || disabledTooltip}
      icon={iconIsImage ? option.icon : undefined}
      iconNode={iconIsImage ? undefined : <Icon style={{ height: 27, width: 27 }} />}
      iconSx={iconIsImage ? undefined : {
        bgcolor: iconConfig.bg,
        borderColor: iconConfig.border,
        color: iconConfig.color,
      }}
      onClick={option?.handleClick}
      subtitle={option?.subtitle}
      text={option?.text}
      title={option?.title}
    />
  );
}

export default function ProductOptionGrid({
  disabledTooltip = "Funcionalidade não disponível no seu plano.",
  emptyMessage = "Nenhuma opção encontrada.",
  items,
  loading = false,
  loadingCount = 3,
  loadingMode = "replace",
  sx,
}) {
  const options = Array.isArray(items) ? items : [];
  const appendLoading = loading && loadingMode === "append";
  const showSkeletonOnly = loading && loadingMode !== "append";
  const skeletonCount = Math.max(loadingCount, 0);

  return (
    <Box
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3, md: 4 },
          width: "100%",
        },
        sx,
      ]}
    >
      <Box
        sx={{
          display: "grid",
          gap: { xs: 1.5, md: 2 },
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(auto-fit, minmax(min(100%, 300px), 360px))",
          },
          justifyContent: "start",
          width: "100%",
        }}
      >
        {showSkeletonOnly
          ? Array.from({ length: skeletonCount }).map((_, index) => (
              <ProductAppCardSkeleton key={`product-option-skeleton-${index}`} />
            ))
          : options.map((option) => (
              <ProductOptionCard
                disabledTooltip={disabledTooltip}
                key={option?.key || option?.id || option?.title}
                option={option}
              />
            ))}

        {appendLoading
          ? Array.from({ length: skeletonCount }).map((_, index) => (
              <ProductAppCardSkeleton key={`product-option-append-skeleton-${index}`} />
            ))
          : null}
      </Box>

      {!loading && options.length === 0 ? (
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 14,
            fontWeight: 500,
            py: 4,
            textAlign: "center",
          }}
        >
          {emptyMessage}
        </Typography>
      ) : null}
    </Box>
  );
}
